import { useMemo } from 'react';

import { Button } from '@repo/videa-components';

import { useMicrophoneContext } from '../../contexts/MicrophoneContext';

import { AudioFileUpload } from './AudioFileUpload';

interface ConsentFlowProps {
  monitoringStream: MediaStream | null;
  mediaBlobUrl?: string;
  buttonStyling: string;
  isSheetOpen: boolean;
  isTemplateSelected: boolean;
  isVoiceNotesConsentEnabled: boolean;
  isAudioUploadEnabled?: boolean;
  showDevTools?: boolean;
  onConsentDenied: () => void;
  onStartProviderOnlyRecording: () => void;
  onStartRecording: () => void;
  onAudioFileSelected?: (file: File, blobUrl: string) => void;
  compactMode?: boolean; // Stack buttons vertically for narrow UIs
}

export const ConsentFlow = ({
  monitoringStream,
  mediaBlobUrl,
  buttonStyling,
  isTemplateSelected,
  isSheetOpen,
  isVoiceNotesConsentEnabled,
  isAudioUploadEnabled = false,
  showDevTools = false,
  compactMode = false,
  onConsentDenied,
  onStartProviderOnlyRecording,
  onStartRecording,
  onAudioFileSelected,
}: ConsentFlowProps) => {
  const { canRecord, hasPermission: hasMicrophonePermission } =
    useMicrophoneContext();

  // Only show "Select Microphone First" if user hasn't granted permission.
  const showSelectMicrophoneFirst =
    !monitoringStream && !hasMicrophonePermission;

  // Show loading when we have permission but stream is temporarily null (during state transitions)
  const isStreamLoading = !monitoringStream && hasMicrophonePermission;

  const shouldDisableButtons = useMemo(() => {
    return !isTemplateSelected || !monitoringStream || !canRecord;
  }, [isTemplateSelected, monitoringStream, canRecord]);

  const renderAudioUpload = () => {
    if (!isAudioUploadEnabled || !onAudioFileSelected || !showDevTools)
      return null;

    return (
      <div className="vai-mt-4 vai-pt-4 vai-border-t vai-border-gray-200 vai-w-full vai-flex vai-flex-col vai-items-center">
        <p className="vai-text-sm vai-text-gray-500 vai-mb-2">
          Or upload a pre-recorded audio file
        </p>
        <AudioFileUpload
          onFileSelected={onAudioFileSelected}
          isDisabled={!isTemplateSelected}
          size={isSheetOpen ? 'default' : 'sm'}
        />
      </div>
    );
  };

  return (
    <div className="vai-flex vai-flex-col vai-items-center">
      {isVoiceNotesConsentEnabled ? (
        <>
          <div className="vai-flex vai-justify-center vai-items-center vai-bg-amber-50 vai-text-amber-600 vai-border vai-border-amber-100 vai-rounded-md vai-px-2 vai-py-1 vai-mb-6 ai-text-sm vai-w-full">
            <p className="vai-text-center">
              Obtain recording consent: <br />
              &quot;
              <span className="vai-font-bold">
                Can we record this visit for note taking only, so we can focus
                on you?&quot;
              </span>
            </p>
          </div>

          <div
            className={
              compactMode
                ? 'vai-flex vai-flex-col vai-gap-2 vai-w-full'
                : 'vai-flex vai-flex-row vai-gap-x-3'
            }
          >
            <Button
              onClick={onStartRecording}
              variant={mediaBlobUrl ? 'sheet' : 'default'}
              className={`${buttonStyling} hover:vai-ring-primary/50 ${compactMode ? 'vai-w-full' : ''}`}
              size={isSheetOpen ? 'default' : 'sm'}
              disabled={shouldDisableButtons}
              loading={isStreamLoading}
            >
              {showSelectMicrophoneFirst
                ? 'Select Microphone First'
                : 'Yes, Start Recording'}
            </Button>

            <Button
              onClick={onStartProviderOnlyRecording}
              variant={'sheet'}
              size={isSheetOpen ? 'default' : 'sm'}
              className={compactMode ? 'vai-w-full' : ''}
              disabled={shouldDisableButtons}
            >
              Provider Only Recording
            </Button>

            <Button
              onClick={onConsentDenied}
              variant={'sheet'}
              size={isSheetOpen ? 'default' : 'sm'}
              className={compactMode ? 'vai-w-full' : ''}
            >
              Consent Denied
            </Button>
          </div>
          {renderAudioUpload()}
        </>
      ) : (
        <>
          <Button
            onClick={onStartRecording}
            variant={mediaBlobUrl ? 'sheet' : 'default'}
            className={`${buttonStyling} hover:vai-ring-primary/50`}
            size={isSheetOpen ? 'default' : 'sm'}
            disabled={shouldDisableButtons}
            loading={isStreamLoading}
          >
            {showSelectMicrophoneFirst
              ? 'Select Microphone First'
              : 'Start Recording'}
          </Button>
          {renderAudioUpload()}
        </>
      )}
    </div>
  );
};
